/**
 * AllVisitsPageNew — paginated list of all visits with pull-to-refresh, swipeable cards, and search.
 * Full feature parity with old AllVisitsPage.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useVisits } from '../hooks/useVisits';
import { useDeleteVisit } from '../hooks/useVisits';
import { PullToRefresh } from '../components/shared/PullToRefresh';
import { SwipeableCard } from '../components/shared/SwipeableCard';
import { SearchInput } from '../components/shared/SearchInput';
import { EmptyState } from '../components/shared/EmptyState';
import { Pagination } from '../components/shared/Pagination';
import { VisitCard } from '../design-system/primitives/VisitCard';
import { Ticket } from 'lucide-react';

const PAGE_SIZE = 10;


export function AllVisitsPageNew(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [page, setPage] = React.useState(0);
  const { data: visits = [], isLoading } = useVisits();
  const deleteVisit = useDeleteVisit();

  const handleRefresh = React.useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: ['visits'] });
  }, [queryClient]);

  const filteredVisits = React.useMemo(() => {
    return visits.filter((v) =>
      v.parkName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.visitDate.includes(searchQuery)
    );
  }, [visits, searchQuery]);

  const sortedVisits = React.useMemo(() => {
    return [...filteredVisits].sort((a, b) => new Date(a.visitDate).getTime() - new Date(b.visitDate).getTime());
  }, [filteredVisits]);

  const paginatedVisits = sortedVisits.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">All Visits</h1>
          <button onClick={() => navigate('/create')}
            className="px-4 py-2 bg-disney-blue-600 text-white rounded-xl hover:bg-disney-blue-700 transition-colors text-sm font-medium">
            New Visit
          </button>
        </div>

        <div className="mb-6">
          <SearchInput value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery('')} placeholder="Search parks or dates..." />
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton-queue-item">
                <div className="skeleton-queue-info">
                  <div className="skeleton-queue-header skeleton" />
                  <div className="skeleton-queue-status skeleton" />
                </div>
                <div className="skeleton-queue-actions">
                  <div className="skeleton-wait-time skeleton" />
                  <div className="skeleton-remove-button skeleton" />
                </div>
              </div>
            ))}
          </div>
        ) : sortedVisits.length === 0 ? (
          <EmptyState icon={Ticket} iconColor="text-gray-400" title="No visits found"
            subtitle={searchQuery ? 'Try a different search term' : 'Create your first park visit to get started.'}
            action={!searchQuery ? { label: 'Create Visit', onClick: () => navigate('/create') } : undefined} />
        ) : (
          <>
            <ul className="space-y-3">
              {paginatedVisits.map((visit) => (
                <li key={visit.id}>
                  <SwipeableCard onSwipeLeft={() => deleteVisit.mutate(visit.id)}>
                    <VisitCard
                      parkName={visit.parkName}
                      visitDate={visit.visitDate}
                      visitPeriod={visit.visitPeriod as 'MORNING' | 'MIDDAY' | 'EVENING' | undefined}
                      attractionCount={visit.selectedAttractionCount ?? 0}
                      noRing
                      onClick={() => navigate(`/visit/${visit.id}`)}
                      aria-label={`View details for ${visit.parkName} on ${visit.visitDate}`}
                    />
                  </SwipeableCard>
                </li>
              ))}
            </ul>
            <Pagination page={page} totalItems={sortedVisits.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
          </>
        )}
      </div>
    </PullToRefresh>
  );
}
